
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Clock } from "lucide-react";
import { useActivePromotions } from "@/hooks/usePromotions";
import { format } from "date-fns";

interface BookingServiceDetailsProps {
  selectedService?: {
    id: string;
    name: string;
    duration_minutes: number;
    price: number;
  };
  provider?: {
    name: string;
    address: string;
  };
  selectedDate?: Date;
}

const BookingServiceDetails = ({ selectedService, provider, selectedDate }: BookingServiceDetailsProps) => {
  const dateString = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
  const { data: promotions } = useActivePromotions(selectedService?.id, dateString);
  const activePromotion = promotions && promotions.length > 0 ? promotions[0] : null;

  const calculateDiscountedPrice = (originalPrice: number) => {
    if (!activePromotion) return originalPrice;
    
    if (activePromotion.discount_type === 'percentage') {
      return originalPrice * (1 - activePromotion.discount_percentage! / 100);
    } else {
      return Math.max(0, originalPrice - activePromotion.discount_amount!);
    }
  };

  const originalPrice = selectedService?.price || 0;
  const discountedPrice = calculateDiscountedPrice(originalPrice);
  const hasPromotion = !!activePromotion;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileText className="h-5 w-5" />
          <span>Service Details</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg">{selectedService?.name || "Service"}</h3>
            <p className="text-gray-600">{provider?.name}</p>
            <p className="text-sm text-gray-500">{provider?.address}</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm text-gray-600">{selectedService?.duration_minutes} minutes</span>
            </div>
            <div className="text-right">
              {hasPromotion ? (
                <div>
                  <span className="text-sm text-gray-500 line-through">£{originalPrice.toFixed(2)}</span>
                  <span className="text-lg font-semibold text-green-600 ml-2">£{discountedPrice.toFixed(2)}</span>
                </div>
              ) : (
                <div className="font-semibold text-lg text-gray-900">
                  £{originalPrice.toFixed(2)}
                </div>
              )}
            </div>
          </div>
          {hasPromotion && (
            <div className="mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md inline-block">
              Promotion - {activePromotion.name}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingServiceDetails;
